import { CompanyCreationStatus } from '@bupa-monorepo/shared-types/company-creation-types';
import { Order } from '@bupa-monorepo/shared-types/query-types';
import { SerializedFile } from '@bupa-monorepo/shared-types/utility-types';

export enum CreationType {
  SELF_EMPLOYMENT = 'SELF_EMPLOYMENT',
  COMPANY = 'COMPANY',
}

export enum PricingPackage {
  BASIC = 'BASIC',
  EXTENDED = 'EXTENDED',
}

export const CompanyCreationProductType = {
  CONSULTATION: 'CONSULTATION',
  COMPANY_CREATION: 'COMPANY_CREATION',
};

export interface BackofficeStatusChangeRequest {
  newState: CompanyCreationStatus;
  companyCreationId: string;
}

export interface EditCompanyCreationRequest {
  basicDataSection?: BasicDataSection;
  companyAddressSection?: CompanyAddressSection;
  activitiesSection?: ActivitiesSection;
  taxingSection?: TaxingSection;
  founderSection?: FounderSection;
  executiveDirectorSection?: ExecutiveDirectorSection;
  companyManagerSection?: CompanyManagerSection;
  moneySection?: MoneySection;
  backofficeComment?: string;
  court?: string;
  newAddressCardPictureNeeded?: boolean;
  dateOfEntry?: Date;
  finalStepsSection?: Partial<FinalStepsSection>;
}

export interface InitCompanyCreationRequest {
  pricingPackage: PricingPackage;
}

export interface SelfEmploymentCreationInitialData {
  zipCode: string;
  fieldOfWork: FieldsOfWork | null;
  typeOfTaxation: TypesOfTaxation | null;
  vatCircle: boolean | null;
}

export interface PersistCompanyCreationRequest {
  basicDataSection?: BasicDataSection;
  companyAddressSection?: CompanyAddressSection;
  activitiesSection?: ActivitiesSection;
  taxingSection?: TaxingSection;
  founderSection?: FounderSection;
  executiveDirectorSection?: ExecutiveDirectorSection;
  companyManagerSection?: CompanyManagerSection;
  moneySection?: MoneySection;
  backofficeComment?: string;
  court?: string;
}

export interface SubmitFounderDataRequest {
  name: string;
  email: string;
  phoneNumber: string;
  acceptedGdpr: boolean;
  acceptedMarketing: boolean;
}

export interface GetDealsResponse {
  id: string;
  status: CompanyCreationStatus | SelfEmploymentCreationStatus;
  identifier: string;
  customerName: string;
  companyName: string;
  lastUpdate: Date;
  lawyerName: string;
  type: CreationType;
}

export interface GetLawyersForDealsResponse {
  lawyerId: string;
  lawyerName: string;
}

export interface GetStatusesForDealsRequest {
  type?: CreationType;
}

type StatusForCreationType<T> = T extends CreationType.SELF_EMPLOYMENT
  ? SelfEmploymentCreationStatus
  : T extends CreationType.COMPANY
    ? CompanyCreationStatus
    : never;

export type GetStatusesForDealsResponse<T extends CreationType> =
  StatusForCreationType<T>;

export type GetDealsOrderableProps = Omit<GetDealsResponse, 'type'>;

export type GetDealsRequest = GetStatusesForDealsRequest &
  Partial<Order<GetDealsOrderableProps>> & {
    status?: CompanyCreationStatus | SelfEmploymentCreationStatus;
    lawyerId?: string;
  };

export interface CompanyCreationResponse {
  id: string;
  status: CompanyCreationStatus;
  dateOfConclusionOfContract?: Date /* UTC+0 */;
  isClosed: boolean;
  basicDataSection?: BasicDataSection;
  companyAddressSection?: CompanyAddressSection;
  activitiesSection?: ActivitiesSection;
  taxingSection?: TaxingSection;
  founderSection?: FounderSection;
  executiveDirectorSection?: ExecutiveDirectorSection;
  companyManagerSection?: CompanyManagerSection;
  moneySection?: MoneySection;
  lawyerId?: string;
  documents: CompanyCreationDocument[];
  dateOfEntry?: Date; // UTC+0, Bejegyzés dátuma
  courtDocuments: CourtDocument[];
  finalStepsSection?: FinalStepsSection;
}

export interface GetLawyersResponse {
  lawyers: {
    id: string;
    adminId: string;
    name: string;
    adminEmail: string;
    address: string;
    lawFirm: string;
    phoneNumber: string;
    kaszNumber: string;
    description: string;
    calendlyUrl: string;
    calendlyAccessToken: string | null;
    subjectToVAT: boolean;
    createdAt: Date;
    updatedAt: Date;
  }[];
}

export interface GetLawyerResponse {
  id: string;
  name: string;
  description?: string;
  lawFirm?: string;
  kaszNumber?: string;
  calendlyUrl: string;
}

export interface CompanyCreationAdminResponse extends CompanyCreationResponse {
  lawyerAdminId: string;
  lawyerPhoneNumber: string;
  lawyerEmailAddress: string;
  lawyerName: string;
  identifier: string;
  backofficeComment?: string;
  statusChangeLogs: StatusChangeLogAdminResponse[];
  court?: string;
  lastUpdatedAt: Date;
  contactEmail?: string;
  contactPhone?: string;
  contactName?: string;
  contractQuestion?: string;
}

export enum AdminType {
  SUPERUSER = 'SUPERUSER',
  LAWYER = 'LAWYER',
  CUSTOMER_SERVICE = 'CUSTOMER_SERVICE',
}

export interface CompanyCreationDocument {
  id: string;
  name: string;
  invalid: boolean; // File is flagged for "needs swap or reupload
  signed: boolean; // Is AVDH signed by customer
  type: CompanyCreationDocumentType;
  originalFileName?: string;
  final: boolean;
}

export interface CourtDocument {
  id: string;
  name: string;
  type: CourtDocumentType;
}

export enum CompanyCreationDocumentType {
  INVOICE = 'INVOICE',
  AGENCY_CONTRACT = 'MEGBÍZÁSI_SZERZŐDÉS', // Draft contract
  SIGNATURE_EXECUTIVE = 'ALÁÍRÁS_ÜGYVEZETŐ',
  SIGNATURE_COMPANY_MANAGER = 'ALÁÍRÁS_CÉGVEZETŐ',
  FOUNDING_DOCUMENT = 'ALAPÍTÓ_OKIRAT',
  IDENTIFICATION_DATA_SHEET_FOUNDER = 'AZONOSÍTÁSI_ADATLAP_ALAPÍTÓ',
  IDENTIFICATION_DATA_SHEET_COMPANY_MANAGER = 'AZONOSÍTÁSI_ADATLAP_CÉGVEZETŐ',
  IDENTIFICATION_DATA_SHEET_EXECUTIVE = 'AZONOSÍTÁSI_ADATLAP_ÜGYVEZETŐ',
  ACCEPTANCE_STATEMENT_COMPANY_MANAGER = 'ELFOGADÓ_NYILATKOZAT_CÉGVEZETŐ',
  ACCEPTANCE_STATEMENT_EXECUTIVE = 'ELFOGADÓ_NYILATKOZAT_ÜGYVEZETŐ',
  BRANCH_OFFICE_USAGE_STATEMENT = 'FIÓKTELEP_HASZNÁLATI_NYILATKOZAT',
  HEADQUARTERS_USAGE_STATEMENT = 'SZÉKHELY_HASZNÁLATI_NYILATKOZAT',
  SITE_USAGE_STATEMENT = 'TELEPHELY_HASZNÁLATI_NYILATKOZAT',
  LIST_OF_MEMBERS = 'TAGJEGYZÉK',
  DECLARATION_OF_PAYMENT_OF_SHARE_CAPITAL = 'TÖRZSTŐKE_MEGFIZETÉSI_NYILATKOZAT',
  ADMINISTRATION_SPACE_USAGE_STATEMENT = 'ÜGYINTÉZÉS_HELYHASZNÁLATI_NYILATKOZAT',
  POWER_OF_ATTORNEY = 'ÜGYVÉDI_MEGHATALMAZÁS',
}

export enum CourtDocumentType {
  COURT_DOCUMENT = 'BEJEGYZŐ_VÉGZÉS',
  SIGNATURE_FOLDER = 'ALÁÍRÓMAPPA',
}

export interface PersonDocument {
  id: string;
  name: string;
  invalid: boolean /* File is flagged for "needs swap or reupload" */;
  originalFileName?: string;
}

export interface RelatedCompanyDocument {
  id: string;
  name: string;
  originalFileName?: string;
  invalid: boolean /* File is flagged for "needs swap or reupload" */;
  type: string; // eg.: application/pdf image/png
}

export type PersonOrCompanyDocument = PersonDocument | RelatedCompanyDocument;

export interface CompanyCreationStatusChangeLog {
  id: string;
  oldStatus: CompanyCreationStatus;
  newStatus: CompanyCreationStatus;
  changedByUserId: string;
  createdAt: Date;
}

export interface StatusChangeLogAdminResponse {
  oldStatus: CompanyCreationStatus | SelfEmploymentCreationStatus;
  newStatus: CompanyCreationStatus | SelfEmploymentCreationStatus;
  createdAt: Date;
}

export interface BasicDataSection {
  companyName?: string | null;
  companyEmail?: string | null;
  companyExistsUntil?: Date | null;
  isCompanyExistUntilExist?: boolean | null;
}

export interface CompanyAddressSection {
  isPremiseAddressExist?: boolean | null;
  isBranchOfficeExist?: boolean | null;
  isCompanyAndAdministrationAddressDifferent?: boolean | null;
  companyAddress?: CompanyCreationAddress | null; // székhely
  administrationAddress?: CompanyCreationAddress | null;
  premiseAddress?: CompanyCreationAddress | null; // telephely
  branchOfficeAddress?: CompanyCreationAddress | null; // fióktelep
}

export interface ActivitiesSection {
  mainActivity?: ICompanyCreationActivities | null;
  otherActivities?: ICompanyCreationActivities[] | null;
}

export interface TaxingSection {
  taxingFormat?: TaxingFormats | null;
  vatGroup?: VatType | null;
  moneyCirculation?: boolean | null;
  socialTaxNumberNeeded?: boolean | null;
  taxNumber?: string | null;
}

export interface FounderSection {
  //alapító
  founderPerson?: PersonData | null;
  founderCompany?: CompanyData | null;
  isFounderACompany?: boolean | null;
}

export interface ExecutiveDirectorSection {
  //ügyvezető
  isFounderAndExecutiveDirectorDifferent?: boolean | null;
  hasExecutiveDirectorTerminationDate?: boolean | null;
  executiveDirectorTerminationDate?: Date | null;
  executiveDirectorPretense?: ExecutiveDirectorPretense | null;
  has36hoursJob?: boolean | null;
  isExecutiveDirectorACompany?: boolean | null;
  executiveDirector?: PersonData | null;
  executiveDirectorCompany?: CompanyData | null;
}

export interface CompanyManagerSection {
  //cégvezető
  noCompanyManagerNeeded?: boolean | null;
  companyManager?: PersonData | null;
}

export interface MoneySection {
  totalShareCapital?: number | null;

  totalCash?: number | null;
  onlyCash?: boolean | null;
  cashOnBankAccount?: boolean | null;

  totalApport?: number | null;
  apports?: Apport[] | null;

  totalShareCapitalAvailable?: boolean | null;
  totalShareCapitalAvailableAt?: Date | null;
  totalShareCapitalAvailableAmount?: number | null;
}

export interface Apport {
  id?: string | null;

  name: string | null;
  value: number | null;

  temp?: boolean | null;
  deleted?: boolean | null;
}

export interface CompanyData {
  companyName?: string | null;
  companyRegistrationNumber?: string | null;
  companyTaxNumber?: string | null;
  mainActivity?: string | null;
  companyAddress?: CompanyCreationAddress | null;
  nameOfAuthorizedPerson?: string | null;
  statusOfAuthorizedPerson?: string | null;
  addressOfAuthorizedPerson?: Address | null;
  idCardFront?: RelatedCompanyDocument;
  idCardFrontId?: string | null;
  idCardBack?: RelatedCompanyDocument;
  idCardBackId?: string | null;
  addressCardBack?: RelatedCompanyDocument;
  addressCardBackId?: string | null;
  taxCardFront?: RelatedCompanyDocument;
  taxCardFrontId?: string | null;
  companyStatement?: RelatedCompanyDocument;
  companyStatementId?: string | null;
  signature?: RelatedCompanyDocument;
  signatureId?: string | null;
}

export interface PersonData {
  name?: string | null;
  birthName?: string | null;
  birthPlace?: string | null;
  birthDate?: Date | null;
  nameOfMother?: string | null;
  address?: Address | null;
  nationality?: string | null;
  idCardNumber?: string | null;
  addressCardNumber?: string | null;
  taxId?: string | null;
  isPublicFigure?: boolean | null;
  idCardFront?: PersonDocument;
  idCardFrontId?: string | null;
  idCardBack?: PersonDocument;
  idCardBackId?: string | null;
  addressCardBack?: PersonDocument;
  addressCardBackId?: string | null;
  taxCardFront?: PersonDocument;
  taxCardFrontId?: string | null;
}

export type CompanyCreationAddressType =
  | 'companyAddress'
  | 'administrationAddress'
  | 'premiseAddress'
  | 'branchOfficeAddress';

export interface PatchPersonDocumentRequest {
  file?: SerializedFile | null;
  originalFileName?: string | null;
}

export interface PatchPersonDocumentResponse {
  id: string | null;
}

export enum AcceptionAction {
  ACCEPT = 'ACCEPT',
  DECLINE = 'DECLINE',
}
export interface OfferAcceptionRequest {
  action: AcceptionAction;
  lawyerId: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
}

export interface BookAppointmentRequest {
  event: string;
  payload: BookAppointmentPayload;
}

export interface BookAppointmentPayload {
  event: BookAppointmentPayloadItem;
  invitee?: BookAppointmentPayloadItem;
}

export interface BookAppointmentPayloadItem {
  uri: string;
}

export interface UploadCompanyCreationDocumentRequest {
  file?: SerializedFile | null;
  fileName: string;
  type: CompanyCreationDocumentType;
  signed: boolean;
  final: boolean;
}

export interface SendContractQuestionRequest {
  question: string;
}

export interface BackofficeUploadCompanyCreationDocumentRequest {
  file?: SerializedFile | null;
  fileName: string;
  type: CompanyCreationDocumentType;
  signed: boolean;
  final: boolean;
  companyCreationId: string;
}

export interface BackofficeUploadCourtDocumentRequest {
  file?: SerializedFile | null;
  fileName: string;
  type: CourtDocumentType;
  companyCreationId: string;
}

export enum DealType {
  EV = 'EV',
  KFT = 'KFT',
}

export interface UploadCompanyCreationDocumentResponse {
  id: string | null;
}

export interface UploadCourtDocumentResponse {
  id: string | null;
}

export interface IActivities {
  code: string;
  name: string;
  licenseNeeded: boolean;
  registerNeeded: boolean;
}

export interface ICompanyCreationActivities extends Partial<IActivities> {
  id?: string;
}

export enum ExecutiveDirectorPretense {
  megbízási_szerződés = 'megbízási szerződés',
  munkaviszony = 'munkaviszony',
}

export const ExecutiveDirectorPretenseLabel: {
  [key in ExecutiveDirectorPretense]: string;
} = {
  [ExecutiveDirectorPretense.megbízási_szerződés]: 'Megbízási szerződés',
  [ExecutiveDirectorPretense.munkaviszony]: 'Munkaviszony',
};

export enum ActivityType {
  main = 'main',
  other = 'other',
}

export enum VatType {
  igen = 'Igen',
  alanyi_mentes = 'Alanyi mentes (AAM)',
  tárgyi_mentes = 'Tárgyi mentes (TAM)',
}

export enum TaxingFormats {
  TAO = 'Társasági adó (TAO)',
  KIVA = 'Kisvállalati adó (KIVA)',
}

export enum Pretenses {
  bérleti = 'bérleti',
  szívességi = 'szívességi',
}

export type Status = {
  done?: CompanyCreationStatus | SelfEmploymentCreationStatus;
  inProgress?: CompanyCreationStatus | SelfEmploymentCreationStatus;
  failed?: CompanyCreationStatus | SelfEmploymentCreationStatus;
};

export interface EditSelfEmploymentCreationRequest {
  personalDataSection?: PersonalData;
  selfEmploymentData?: SelfEmploymentDataSection;
  finalStepsSection?: Partial<FinalStepsSection>;
}

export interface PersistSelfEmploymentCreationRequest {
  personalDataSection?: PersonalData;
  selfEmploymentData?: SelfEmploymentDataSection;
}

export interface SubmitSelfEmploymentCreationRequest {
  pricingPackage: PricingPackage;
  taxConsulting?: boolean;
  couponCode?: string;
  name: string;
  email: string;
  phoneNumber: string;
  acceptedGdpr: boolean;
  acceptedMarketing: boolean;
}

export enum SelfEmploymentCreationStatus {
  DRAFT = 'KITÖLTÉS_ALATT',
  BACKOFFICE_REVIEW = 'ÜGYFÉLSZOLGÁLAT_ELLENŐRZÉSE_ALATT',
  FAILED = 'MEGHIÚSULT',
  ESTABLISHED = 'MEGALAPULT',
}

export interface SelfEmploymentCreationResponse {
  status: SelfEmploymentCreationStatus;
  personalDataSection?: PersonalData;
  selfEmploymentData?: SelfEmploymentDataSection;
  dateOfEntry?: Date; // Bejegyyzés dátuma
  finalStepsSection?: FinalStepsSection;
  contactName: string | null;
  showEstabilishedMessage: boolean | null;
}

export interface SelfEmploymentCreationAdminResponse
  extends SelfEmploymentCreationResponse {
  id: string;
  identifier: string;
  backofficeComment?: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  lastUpdatedAt: Date;
  statusChangeLogs: StatusChangeLogAdminResponse[];
}

export interface IBackofficeCommentRequestBody {
  backofficeComment: string;
}

export interface DateOfEntryRequestBody {
  dateOfEntry: Date;
}

export interface SelfEmploymentTaxNumberRequestBody {
  selfEmploymentCreationId: string;
  estabilishedSelfEmploymentTaxNumber: string;
}

export interface PersonalData {
  address?: Address | null;
  fieldOfWork?: FieldsOfWork | null;
}

export interface Address {
  country?: Countries | null;
  postalCode?: string | null;
  city?: string | null;
  typeOfPublicArea?: CompanyCreationTypeOfPublicArea | null;
  nameOfPublicArea?: string | null;
  houseNumber?: string | null;
  building?: string | null;
  staircase?: string | null;
  floor?: string | null;
  door?: string | null;
  description?: string | null;
}

export interface CompanyCreationAddress extends Address {
  pretense?: Pretenses | null;
}

/**
 * @deprecated
 */
export interface DocumentSection {
  idCardNumber?: string | null;
  addressCardNumber?: string | null;
  healthInsuranceCardNumber?: string | null;
  taxNumber?: string | null;
}

/**
 * @deprecated
 */
export interface SelfEmploymentActivitiesSection {
  mainActivity?: ICompanyCreationActivities | null;
  otherActivities?: ICompanyCreationActivities[] | null;
}

/**
 * @deprecated
 */
export enum WorkingTypes {
  Főfoglalkozásként = 'Főfoglalkozásként',
  Heti_36_órás_munkaviszony = 'Heti 36 órát meghaladó munkaviszony mellett',
  Nappali_tagozatos_tanulmányok_folytatása_mellett = 'Nappali tagozatos tanulmányok folytatása mellett',
  Nyugdíjasként = 'Nyugdíjasként',
}

/**
 * @deprecated
 */
export const WorkingTypesLabel: {
  [key in WorkingTypes]: string;
} = {
  [WorkingTypes.Főfoglalkozásként]: 'Főfoglalkozásként',
  [WorkingTypes.Heti_36_órás_munkaviszony]:
    'Heti min. 36 órás munkaviszony mellett',
  [WorkingTypes.Nappali_tagozatos_tanulmányok_folytatása_mellett]:
    'Nappali tagozatos hallgatói jogviszony mellett',
  [WorkingTypes.Nyugdíjasként]: 'Nyugdíj mellett',
};

export interface SelfEmploymentDataSection {
  estabilishedSelfEmploymentTaxNumber?: string | null;
  typeOfTaxation?: TypesOfTaxation | null;
  vatCircle?: boolean | null;
}

export interface FinalStepsSection {
  isBankAccountOpen: boolean | null;
  isAccountantFound: boolean | null;
  isKamaraRegistered: boolean | null;
  isKshLoggedIn: boolean | null;
  isNavLoggedIn: boolean | null;
  isHipaLoggedIn: boolean | null;
  isCegkapuRegistered: boolean | null;
}

export interface IActivities {
  code: string;
  name: string;
  licenseNeeded: boolean;
  registerNeeded: boolean;
}

export interface ICompanyCreationActivities extends Partial<IActivities> {
  id?: string;
}

export enum PlaceOfAdministration {
  PREMISE_SAME_WITH_ADMINISTRATION = 'A székhely az ügyintézés helye',
  PREMISE_DIFFER_FROM_ADMINISTRATION = 'Az ügyintézés helye eltér a székhelyétől, más címet adok meg',
}

export enum PlaceOfPremise {
  WILL_BE = 'Lesz telephelye a vállalkozásnak',
  WILL_NOT_BE = 'Nem lesz telephelye a vállalkozásnak',
}

export enum PlaceOfBranchOffice {
  WILL_NOT_BE = 'Nem lesz fióktelepe a vállalkozásnak',
  WILL_BE = 'Lesz fióktelepe a vállalkozásnak',
}

export enum Founding {
  JUST_CASH = 'Csak készpénz',
  CASH_AND_APPORT = 'Készpénz és nem pénzbeli hozzájárulás (apport)',
}
export enum FoundingPart {
  BANK_ACCOUNT = 'A törzstőke készpénz részét bankszámlán bocsátom a vállalkozás rendelkezésére',
  HOUSE_CASH = 'A törzstőke készpénz részét házipénztárban bocsátom a vállalkozás rendelkezésre',
}
export enum FoundingCashAmount {
  TOTAL = 'Teljes készpénz összeg',
  PARTIAL = 'Csak a készpénz összeg egy része',
}

export enum Descriptions {
  AT_LEAST_3_MILLION = 'Min. 3 millió forintot kell biztosítani.',
  APPORT_PARTS = 'A vállalkozás törzstőkéje nem csak készpénzből tevődhet össze, hanem készpénzből és valamilyen nagyobb vagyontárgyból (pl. ingatlan, gépjármű) is.',
  CASH_PART_TYPE = 'A vállalkozás törzstőkéjét a cég saját házipénztárába is befizetheted (ilyen esetben ezt ügyvezetői nyilatkozat igazolja) és bankszámlára is utalhatod.',
  APPORT_TYPES = 'Az apport opcionális és bármilyen nagyobb értékű vagyontárgy lehet, például ingatlan vagy gépjármű. Ha apportot is szeretnél bevonni a társaság törzstőkéjébe, akkor az apport nevét és értékét a lenti “Apport hozzáadása” gombra kattintva kell megadnod. Több tételt is rögzíthetsz.',
  CASH_AMOUNT = 'Alapításkor nem kötelező a vállalkozás rendelkezésére bocsátani a teljes készpénz összeget, de hasznos, mert a működés ebből fedezhető. Ügyelj arra, hogy két éven belül rendelkezésre kell állnia a teljes összegnek.',
}

export type OfficialsFormValues = Pick<
  CompanyCreationResponse,
  'founderSection' | 'executiveDirectorSection' | 'companyManagerSection'
>;

export enum CEO_TASK_DURATION {
  INDEFINITE_TIME = 'Határozatlan idő',
  DEFINITE_TIME = 'Határozott idő',
}

export enum APPOINT_COMPANY_MANAGER {
  YES = 'Szeretnék kinevezni cégvezetőt',
  NO = 'Nem szeretnék kinevezni cégvezetőt',
}

export enum EXECUTIVE_EXCEEDING_36HOURS_EMPLOYMENT_REL {
  YES = 'Heti 36 órát meghaladó munkaviszony',
  NO = 'Heti 36 órát nem elérő munkaviszony',
}

export enum EXECUTIVE_PERFORM_DUTIES {
  AGENCY_CONTRACT = 'Megbízási szerződés',
  EMPLOYMENT_REL = 'Munkaviszony',
}

export enum PERSON_TYPE {
  PRIVATE_PERSON = 'Magánszemély',
  LEGAL_PERSON = 'Jogi személy (cég)',
}

export enum FOUNDER_DIFF_FROM_MANAGER {
  YES = 'Az alapító és az ügyvezető eltér',
  NO = 'Az alapító és ügyvezető ugyanaz a személy',
}

export enum PUBLIC_FIGURE_TYPE {
  YES = 'Igen, annak minősül',
  NO = 'Nem, nem minősül annak',
}

export enum PUBLIC_FIGURE_TITLES {
  FOUNDER_PERSON = 'A tényleges tulajdonos a Pmt. 3. § 38. pontja szerinti kiemelt közszereplőnek vagy annak közeli hozzátartozójának minősül?',
  EXECUTIVE_DIRECTOR = 'Az ügyvezető a Pmt. 3. § 38. pontja szerinti kiemelt közszereplőnek vagy annak közeli hozzátartozójának minősül?',
  COMPANY_MANAGER = 'A cégvezető a Pmt. 3. § 38. pontja szerinti kiemelt közszereplőnek vagy annak közeli hozzátartozójának minősül?',
}

export enum Countries {
  Magyarország = 'Magyarország',
  Andorra = 'Andorra',
  Egyesült_Arab_Emírségek = 'Egyesült Arab Emírségek',
  Afganisztán = 'Afganisztán',
  Antigua = 'Antigua',
  Anguilla = 'Anguilla',
  Albánia = 'Albánia',
  Örményország = 'Örményország',
  Holland_Antillák = 'Holland Antillák',
  Angola = 'Angola',
  Antarktisz = 'Antarktisz',
  Argentína = 'Argentína',
  Amerikai_Szamoa = 'Amerikai Szamoa',
  Ausztria = 'Ausztria',
  Ausztrália = 'Ausztrália',
  Aruba = 'Aruba',
  Azerbajdzsán = 'Azerbajdzsán',
  Bosznia_Hercegovina = 'Bosznia-Hercegovina',
  Barbados = 'Barbados',
  Banglades = 'Banglades',
  Belgium = 'Belgium',
  Burkina_Faso = 'Burkina Faso',
  Bulgária = 'Bulgária',
  Bahrein = 'Bahrein',
  Burundi = 'Burundi',
  Benin = 'Benin',
  Bermuda = 'Bermuda',
  Brunei = 'Brunei',
  Bolívia = 'Bolívia',
  Brazília = 'Brazília',
  Bahama_szigetek = 'Bahama-szigetek',
  Bhután = 'Bhután',
  Bouvet_sziget = 'Bouvet-sziget',
  Botswana = 'Botswana',
  Fehéroroszország = 'Fehéroroszország',
  Belize = 'Belize',
  Kanada = 'Kanada',
  Coco_sziget = 'Coco-sziget',
  Kongói_Demokratikus_Köztársaság = 'Kongói Demokratikus Köztársaság',
  Közép_afrikai_Köztársaság = 'Közép-afrikai Köztársaság',
  Kongó = 'Kongó',
  Svájc = 'Svájc',
  Elefántcsontpart = 'Elefántcsontpart',
  Cook_szigetek = 'Cook-szigetek',
  Chile = 'Chile',
  Kamerun = 'Kamerun',
  Kína = 'Kína',
  Kolumbia = 'Kolumbia',
  Costa_Rica = 'Costa Rica',
  Szerbia_és_Montenegro = 'Szerbia és Montenegro',
  Kuba = 'Kuba',
  Zöld_foki_Köztársaság = 'Zöld-foki Köztársaság',
  Karácsony_sziget = 'Karácsony-sziget',
  Ciprus = 'Ciprus',
  Csehország = 'Csehország',
  Németország = 'Németország',
  Dzsibuti = 'Dzsibuti',
  Dánia = 'Dánia',
  Dominikai_Közösség = 'Dominikai Közösség',
  Dominikai_Köztársaság = 'Dominikai Köztársaság',
  Algéria = 'Algéria',
  Ecuador = 'Ecuador',
  Észtország = 'Észtország',
  Egyiptom = 'Egyiptom',
  Eritrea = 'Eritrea',
  Spanyolország = 'Spanyolország',
  Etiópia = 'Etiópia',
  Finnország = 'Finnország',
  Fidzsi_szigetek = 'Fidzsi-szigetek',
  Falkland_szigetek = 'Falkland-szigetek',
  Mikronézia = 'Mikronézia',
  Faroe_Islands = 'Faroe Islands',
  Franciaország = 'Franciaország',
  Gabon = 'Gabon',
  Nagy_Britannia = 'Nagy-Britannia',
  Grenada = 'Grenada',
  Grúzia = 'Grúzia',
  Guernsey = 'Guernsey',
  Ghána = 'Ghána',
  Gibraltár = 'Gibraltár',
  Grönland = 'Grönland',
  Gambia = 'Gambia',
  Guinea = 'Guinea',
  Egyenlítői_Guinea = 'Egyenlítői-Guinea',
  Görögország = 'Görögország',
  Déli_Georgia_és_Déli_Sandwich_szigetek = 'Déli-Georgia és Déli-Sandwich-szigetek',
  Guatemala = 'Guatemala',
  Guam = 'Guam',
  Bissau_Guinea = 'Bissau-Guinea',
  Guyana = 'Guyana',
  Hongkong = 'Hongkong',
  Heard_sziget_és_McDonald_szigetek = 'Heard-sziget és McDonald-szigetek',
  Honduras = 'Honduras',
  Horvátország = 'Horvátország',
  Haiti = 'Haiti',
  Indonézia = 'Indonézia',
  Írország = 'Írország',
  Izrael = 'Izrael',
  Man_sziget = 'Man sziget',
  India = 'India',
  Brit_Indiai_Óceániai_Terület = 'Brit Indiai-Óceániai Terület',
  Irak = 'Irak',
  Irán = 'Irán',
  Izland = 'Izland',
  Olaszország = 'Olaszország',
  Jersey = 'Jersey',
  Jamaica = 'Jamaica',
  Jordánia = 'Jordánia',
  Japán = 'Japán',
  Kenya = 'Kenya',
  Kirgizisztán = 'Kirgizisztán',
  Kambodzsa = 'Kambodzsa',
  Kiribati_Köztársaság_Tuvalu = 'Kiribati Köztársaság Tuvalu',
  Comore_szigetek = 'Comore-szigetek',
  Saint_Christopher_és_Nevis = 'Saint Christopher és Nevis',
  Koreai_NDK = 'Koreai NDK',
  Korea = 'Korea',
  Kuvait = 'Kuvait',
  Kajmán_szigetek = 'Kajmán-szigetek',
  Kazahsztán = 'Kazahsztán',
  Laosz = 'Laosz',
  Libanon = 'Libanon',
  Saint_Lucia = 'Saint Lucia',
  Liechtenstein = 'Liechtenstein',
  Sri_Lanka = 'Sri Lanka',
  Libéria = 'Libéria',
  Lesotho = 'Lesotho',
  Litvánia = 'Litvánia',
  Luxemburg = 'Luxemburg',
  Lettország = 'Lettország',
  Líbia = 'Líbia',
  Marokkó = 'Marokkó',
  Moldova = 'Moldova',
  Malgas_Köztársaság = 'Malgas Köztársaság',
  Marshall_szigetek = 'Marshall-szigetek',
  Macedónia = 'Macedónia',
  Mali = 'Mali',
  Mianmar = 'Mianmar',
  Mongólia = 'Mongólia',
  Macao = 'Macao',
  Északi_Mariana_szigetek = 'Északi-Mariana-szigetek',
  Mauritánia = 'Mauritánia',
  Montserrat = 'Montserrat',
  Málta = 'Málta',
  Mauritius = 'Mauritius',
  Maldív_szigetek = 'Maldív-szigetek',
  Malawi = 'Malawi',
  Mexikó = 'Mexikó',
  Malajzia = 'Malajzia',
  Mozambik = 'Mozambik',
  Namibia = 'Namibia',
  Új_Kaledónia = 'Új-Kaledónia',
  Niger = 'Niger',
  Norfolk_szigetek = 'Norfolk szigetek',
  Nigéria = 'Nigéria',
  Nicaragua = 'Nicaragua',
  Hollandia = 'Hollandia',
  Norvégia = 'Norvégia',
  Nepál = 'Nepál',
  Nauru = 'Nauru',
  Niue = 'Niue',
  Új_Zéland = 'Új-Zéland',
  Omán = 'Omán',
  Panama = 'Panama',
  Peru = 'Peru',
  Francia_Polinézia = 'Francia Polinézia',
  Pápua_Új_Ginea = 'Pápua Új-Ginea',
  Fülöp_szigetek = 'Fülöp-szigetek',
  Pakisztán = 'Pakisztán',
  Lengyelország = 'Lengyelország',
  Pitcairn_szigetek = 'Pitcairn-szigetek',
  Palesztína = 'Palesztína',
  Portugália = 'Portugália',
  Palau = 'Palau',
  Paraguay = 'Paraguay',
  Katar = 'Katar',
  Románia = 'Románia',
  Oroszország = 'Oroszország',
  Ruanda = 'Ruanda',
  Szaúd_Arábia = 'Szaúd-Arábia',
  Salamon_szigetek = 'Salamon-szigetek',
  Seychelle_szigetek = 'Seychelle-szigetek',
  Szudán = 'Szudán',
  Svédország = 'Svédország',
  Szingapúr = 'Szingapúr',
  Szent_Ilona = 'Szent Ilona',
  Szlovénia = 'Szlovénia',
  Szlovákia = 'Szlovákia',
  Sierra_Leone = 'Sierra Leone',
  San_Marino = 'San Marino',
  Szenegál = 'Szenegál',
  Szomália = 'Szomália',
  Suriname = 'Suriname',
  Sao_Tome_és_Princípe = 'Sao Tome és Princípe',
  Salvador = 'Salvador',
  Szíria = 'Szíria',
  Szváziföld = 'Szváziföld',
  Turksés_Caicos_szigetek = 'Turks- és Caicos-szigetek',
  Csád = 'Csád',
  Francia_Déli_Területek = 'Francia Déli Területek',
  Togo = 'Togo',
  Thaiföld = 'Thaiföld',
  Tadzsikisztán = 'Tadzsikisztán',
  Tokelau_szigetek = 'Tokelau-szigetek',
  Türkmenisztán = 'Türkmenisztán',
  Tunézia = 'Tunézia',
  Tonga = 'Tonga',
  Törökország = 'Törökország',
  Trinidad_és_Tobago = 'Trinidad és Tobago',
  Tuvalu = 'Tuvalu',
  Taiwan = 'Taiwan',
  Tanzánia = 'Tanzánia',
  Euró_zóna = 'Euró zóna',
  Ukrajna = 'Ukrajna',
  Uganda = 'Uganda',
  United_States_Minor_Outlying_Islands = 'United States Minor Outlying Islands',
  Amerikai_Egyesült_Államok = 'Amerikai Egyesült Államok',
  Uruguay = 'Uruguay',
  Üzbegisztán = 'Üzbegisztán',
  Vatikán = 'Vatikán',
  Saint_Vincent = 'Saint Vincent',
  Venezuela = 'Venezuela',
  Brit_Virgin_szigetek = 'Brit Virgin-szigetek',
  Amerikai_Virgin_szigetek = 'Amerikai Virgin-szigetek',
  Vietnámi_Köztársaság = 'Vietnámi Köztársaság',
  Vanuatu = 'Vanuatu',
  Wallis_és_Futuna = 'Wallis és Futuna',
  Szamoa = 'Szamoa',
  Jemen = 'Jemen',
  Dél_Afrikai_Köztársaság = 'Dél-Afrikai Köztársaság',
  Zambia = 'Zambia',
  Zimbabwe = 'Zimbabwe',
}

export enum CompanyCreationTypeOfPublicArea {
  utca = 'utca',
  út = 'út',
  tér = 'tér',
  akna = 'akna',
  akna_alsó = 'akna-alsó',
  akna_felső = 'akna-felső',
  alagút = 'alagút',
  alsórakpart = 'alsórakpart',
  arborétum = 'arborétum',
  autóút = 'autóút',
  barakképület = 'barakképület',
  barlang = 'barlang',
  bejáró = 'bejáró',
  bekötőút = 'bekötőút',
  bánya = 'bánya',
  bányatelep = 'bányatelep',
  bástya = 'bástya',
  bástyája = 'bástyája',
  csárda = 'csárda',
  csónakházak = 'csónakházak',
  domb = 'domb',
  dűlő = 'dűlő',
  dűlők = 'dűlők',
  dűlősor = 'dűlősor',
  dűlőterület = 'dűlőterület',
  dűlőút = 'dűlőút',
  egyetemváros = 'egyetemváros',
  egyéb = 'egyéb',
  elágazás = 'elágazás',
  emlékút = 'emlékút',
  erdészház = 'erdészház',
  erdészlak = 'erdészlak',
  erdő = 'erdő',
  erdősor = 'erdősor',
  fasor = 'fasor',
  fasora = 'fasora',
  felső = 'felső',
  forduló = 'forduló',
  főmérnökség = 'főmérnökség',
  főtér = 'főtér',
  főút = 'főút',
  föld = 'föld',
  gyár = 'gyár',
  gyártelep = 'gyártelep',
  gyárváros = 'gyárváros',
  gyümölcsös = 'gyümölcsös',
  gát = 'gát',
  gátsor = 'gátsor',
  gátőrház = 'gátőrház',
  határsor = 'határsor',
  határút = 'határút',
  hegy = 'hegy',
  hegyhát = 'hegyhát',
  hegyhát_dűlő = 'hegyhát dűlő',
  köz = 'köz',
  hrsz = 'hrsz.',
  ház = 'ház',
  hídfő = 'hídfő',
  iskola = 'iskola',
  ipartelep = 'ipartelep',
  játszótér = 'játszótér',
  kapu = 'kapu',
  kastély = 'kastély',
  kert = 'kert',
  kertsor = 'kertsor',
  kerület = 'kerület',
  kilátó = 'kilátó',
  kioszk = 'kioszk',
  kocsiszín = 'kocsiszín',
  kolónia = 'kolónia',
  korzó = 'korzó',
  kultúrpark = 'kultúrpark',
  kunyhó = 'kunyhó',
  kör = 'kör',
  körtér = 'körtér',
  körvasút = 'körvasút',
  körzet = 'körzet',
  körönd = 'körönd',
  körút = 'körút',
  kút = 'kút',
  kültelek = 'kültelek',
  lakóház = 'lakóház',
  lakókert = 'lakókert',
  lakónegyed = 'lakónegyed',
  lakópark = 'lakópark',
  lakótelep = 'lakótelep',
  lejtő = 'lejtő',
  lejáró = 'lejáró',
  liget = 'liget',
  lépcső = 'lépcső',
  major = 'major',
  malom = 'malom',
  menedékház = 'menedékház',
  munkásszálló = 'munkásszálló',
  mélyút = 'mélyút',
  műút = 'műút',
  oldal = 'oldal',
  orom = 'orom',
  park = 'park',
  parkja = 'parkja',
  parkoló = 'parkoló',
  part = 'part',
  pavilon = 'pavilon',
  piac = 'piac',
  pihenő = 'pihenő',
  pince = 'pince',
  pincesor = 'pincesor',
  postafiók = 'postafiók',
  puszta = 'puszta',
  pálya = 'pálya',
  pályaudvar = 'pályaudvar',
  rakpart = 'rakpart',
  repülőtér = 'repülőtér',
  rész = 'rész',
  rét = 'rét',
  sarok = 'sarok',
  sor = 'sor',
  sora = 'sora',
  sportpálya = 'sportpálya',
  sporttelep = 'sporttelep',
  stadion = 'stadion',
  stadionfürdő = 'stadionfürdő',
  sugárút = 'sugárút',
  szer = 'szer',
  sziget = 'sziget',
  szivattyútelep = 'szivattyútelep',
  szállás = 'szállás',
  szállások = 'szállások',
  szél = 'szél',
  szőlő = 'szőlő',
  szőlők = 'szőlők',
  szőlőhegy = 'szőlőhegy',
  sánc = 'sánc',
  sávház = 'sávház',
  sétány = 'sétány',
  tag = 'tag',
  tanya = 'tanya',
  tanyák = 'tanyák',
  telep = 'telep',
  temető = 'temető',
  tere = 'tere',
  tető = 'tető',
  turistaház = 'turistaház',
  téli_kikötő = 'téli kikötő',
  tömb = 'tömb',
  udvar = 'udvar',
  utak = 'utak',
  utcája = 'utcája',
  vadaskert = 'vadaskert',
  vadászház = 'vadászház',
  vasúti_megálló = 'vasúti megálló',
  vasúti_őrház = 'vasúti őrház',
  vasútsor = 'vasútsor',
  vasútállomás = 'vasútállomás',
  vezetőút = 'vezetőút',
  vár = 'vár',
  várköz = 'várköz',
  város = 'város',
  vízmű = 'vízmű',
  völgy = 'völgy',
  zsilip = 'zsilip',
  zug = 'zug',
  állat_és_növénykert = 'állat és növénykert',
  állomás = 'állomás',
  árnyék = 'árnyék',
  árok = 'árok',
  átjáró = 'átjáró',
  őrház = 'őrház',
  őrházak = 'őrházak',
  őrházlak = 'őrházlak',
  útfél = 'útfél',
  útja = 'útja',
  üdülő = 'üdülő',
  üdülő_part = 'üdülő-part',
  üdülő_sor = 'üdülő-sor',
  üdülő_telep = 'üdülő-telep',
}

export enum FieldsOfWork {
  informatika = 'Informatika',
  epitoipar = 'Építőipar',
  e_kereskedelem = 'E-kereskedelem',
  kis_es_nagy_kereskedelem = 'Kis- és nagykereskedelem',
  muveszet = 'Művészet',
  oktatas_kutatas = 'Oktatás, kutatás',
  szallitas_raktarozas = 'Szállítás, raktározás',
  szepsegipar = 'Szépségipar',
  egeszsegugy = 'Egészségügy',
  szallas_vendeglatas = 'Szállás, vendéglátás',
  gyartas_es_feldolgozoipar = 'Gyártás és feldolgozóipar',
  penzugy_biztositas = 'Pénzügy, biztosítás',
  mezo_es_erdogazdalgodas_halaszat = 'Mező- és erdőgazdálkodás, halászat',
  start_up = 'Start up',
  gepjarmu_ipar = 'Gépjármű ipar',
  ingatlankereskedelem = 'Ingatlankereskedelem',
  futar = 'Futár',
  ugyvedi_iroda = 'Ügyvédi iroda',
  egyeb = 'Egyéb',
}

export enum TypesOfTaxation {
  KATA = 'KATA',
  VSZJA = 'VSZJA',
  ATALANYADO = 'Átalányadó',
  KIVA = 'KIVA',
  TAO = 'TAO',
}

export type CompanyCreationOfficialPersonsKeys =
  | keyof Pick<CompanyManagerSection, 'companyManager'>
  | keyof Pick<ExecutiveDirectorSection, 'executiveDirector'>
  | keyof Pick<FounderSection, 'founderPerson'>;

export type CompanyCreationOfficialCompanyKeys =
  | keyof Pick<ExecutiveDirectorSection, 'executiveDirectorCompany'>
  | keyof Pick<FounderSection, 'founderCompany'>;

export type CompanyCreationOfficialsPersonSectionKeys = keyof Pick<
  CompanyCreationResponse,
  'founderSection' | 'executiveDirectorSection' | 'companyManagerSection'
>;

export type CompanyCreationOfficialsCompanySectionKeys = keyof Pick<
  CompanyCreationResponse,
  'founderSection' | 'executiveDirectorSection'
>;

export type CompanyCreationImageKeys = keyof Pick<
  PersonData,
  'idCardBack' | 'idCardFront' | 'taxCardFront' | 'addressCardBack'
>;

export type OfficialDocumentTypePaths =
  | 'founders'
  | 'companyManager'
  | 'executiveDirector';

// set the configurablePage IDs here (not the config-s ID, that comes from the API)
export enum PageIds {
  dashboard = 'clban6j64000208l52d8wfvkd',
  selfEmploymentServiceCardsPage = 'cltyf1030000008k0fftndalu',
  singlePersonStepsAfterRegistration = 'clo4f9ptv000108jr49959vhd',
  selfEmploymentStepsAfterRegistration = 'clo5r5awj000008mg3p3i6to1',
}

export enum SectionIds {
  COMPANY_CREATION_BLOCKS = 'clvqjuddl000008mg67e2fmme',
  SERVICE_CARDS_BLOCKS = 'cltyf42at000108k0afx9eg34',
}

export enum GroupIds {
  TAXATION_HELPER = 'cltyf5qhy000208k013688z54',
  TAXATION_HELPER_ADDITIONAL = 'cltykvh7g000008jq80oofoyw',
  CALLBACK = 'cltyf6g49000408k0ctjn1ojp',
  CALLBACK_ADDITIONAL = 'cltykvtuh000208jqgqr31ubp',
}

export enum SUCCESS_STEP_READY {
  YES = 'Kész',
  NO = 'Nincs kész',
}

export enum DEADLINE_TYPES {
  // this line will be injected next to the calculated day
  TO_BE_DONE_WITHIN_X_DAYS = 'napon belül elvégzendő',
  TO_BE_DONE_TODAY = 'Mai napon elvégzendő',
  EXPIRED = 'Lejárt a határidő',
  DONE = 'Kész',
}

export type CompanyCreationSuccessStepDetailIds = keyof Pick<
  FinalStepsSection,
  | 'isCegkapuRegistered'
  | 'isHipaLoggedIn'
  | 'isKamaraRegistered'
  | 'isKshLoggedIn'
  | 'isNavLoggedIn'
>;

export type SelfEmploymentepDetailIds = keyof Pick<
  FinalStepsSection,
  'isHipaLoggedIn' | 'isKamaraRegistered' | 'isKshLoggedIn' | 'isNavLoggedIn'
>;

export enum MONEY_CIRCULATION_TYPES {
  CASH_FLOW_VAT = 'Pénzforgalmi áfás lesz a vállalkozás',
  NO_CASH_FLOW_VAT = 'Nem lesz pénzforgalmi áfás a vállalkozás',
}

export enum SOCIAL_TAX_NUMBER_NEEDED {
  YES = 'Igénylek',
  NO = 'Nem igénylek',
}
